import Button from "@app/components/button";
import Checkbox from "@app/components/checkbox";
import { FormControl } from "@app/components/form-control";
import Input from "@app/components/input";
import ResetPasswordModal from "@app/components/reset-password-modal/reset-password-modal.component";
import RevokeVipModal from "@app/components/revoke-vip-modal";
import ChevronDownArrow from "@app/components/select/chevron-down";
import { CustomSelect } from "@app/components/select/custom-select";
import { addToast } from "@app/components/toast/toast.service";
import {
  Role,
  SystemMessage,
  RoleOptions,
  StatusOptions,
  UserOptions,
} from "@app/constants";
import AuthService from "@app/services/http/auth.service";
import UserService from "@app/services/http/user.service";
import { openPortalDialog } from "@app/services/modal.service";
import { UpdateUser, User } from "@app/types";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import { Form, Formik, FormikContextType } from "formik";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

const InfoUserTab = () => {
  const [userInfo, setUserInfo] = useState<User>();
  const [showMaxVoiceTrain, setShowMaxVoiceTrain] = useState(false);
  const [isSelectedRoleAuthor, setIsSelectedRoleAuthor] = useState(false);
  const formRef = useRef<FormikContextType<UpdateUser>>(null);
  const { userId } = useParams();

  const [update, forceUpdate] = useForceUpdate();

  useEffect(() => {
    AuthService.getUserById(Number(userId)).then((res) => {
      setUserInfo(res.data);
      setShowMaxVoiceTrain(res.data.role === Role.AUTHOR ? true : false);
      setIsSelectedRoleAuthor(res.data.role === Role.AUTHOR ? true : false);
    });
  }, [update]);

  const handleRoleOptionChange = (e: number | string) => {
    if (formRef.current) {
      formRef.current.setFieldValue("role", e);

      if (e === Role.AUTHOR) {
        setShowMaxVoiceTrain(true);
        formRef.current.setFieldValue("maxVoiceTrain", 0);
        formRef.current.setFieldValue("canCreateAudio", false);
        formRef.current.setFieldValue("canUploadAudio", false);
      } else {
        setShowMaxVoiceTrain(false);
        formRef.current.setFieldValue("maxVoiceTrain", undefined);
        formRef.current.setFieldValue("canCreateAudio", false);
        formRef.current.setFieldValue("canUploadAudio", false);
      }
    }

    setIsSelectedRoleAuthor(e === Role.AUTHOR ? true : false);
  };

  const handleSubmit = async (values: UpdateUser) => {
    if (isSelectedRoleAuthor) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { email, phone, ...data } = values;

      if (!data.affAlias) {
        data.affAlias = undefined;
      }

      if (!data.affVipTime) {
        data.affVipTime = undefined;
      }

      await UserService.updateUser(Number(userId), data);
    } else {
      const { ...data } = values;

      if (!data.affAlias) {
        data.affAlias = undefined;
      }

      if (!data.affVipTime) {
        data.affVipTime = undefined;
      }

      await UserService.updateUser(Number(userId), data);
    }

    addToast({ text: SystemMessage.UPDATE_SUCCESSFULLY });
    forceUpdate();
  };

  const handleReset = () => {
    const resetPasswordModalObs = openPortalDialog(ResetPasswordModal);

    resetPasswordModalObs.afterClosed().subscribe((data) => {
      if (data?.newPassword)
        UserService.resetPassword(Number(userId), data.newPassword).then(() => {
          addToast({ text: SystemMessage.ADD_SUCCESSFULLY });
          forceUpdate();
        });
    });
  };

  const handleRevoke = () => {
    const revokeVipModalObs = openPortalDialog(RevokeVipModal);

    revokeVipModalObs.afterClosed().subscribe((data) => {
      if (data?.confirm)
        UserService.revokeVIP(Number(userId)).then(() => {
          addToast({ text: SystemMessage.ADD_SUCCESSFULLY });
          forceUpdate();
        });
    });
  };

  return (
    <div className="flex gap-32">
      <div>
        <Formik
          innerRef={formRef}
          initialValues={{
            username: userInfo?.username ?? "",
            fullName: userInfo?.fullName ?? "",
            phone: userInfo?.phone ?? "",
            email: userInfo?.email ?? "",
            provider: userInfo?.provider ?? "",
            point: userInfo?.point ?? 0,
            isVip: userInfo?.isVip.toString() ?? true,
            role: userInfo?.role ?? Role.USER,
            status: userInfo?.status.toString() ?? "",
            maxVoiceTrain: userInfo?.maxVoiceTrain || 0,
            canCreateAudio: Boolean(userInfo?.canCreateAudio),
            canUploadAudio: Boolean(userInfo?.canUploadAudio),
            thumbnail: undefined,
            affAlias: userInfo?.affAlias ?? "",
            affVipTime: userInfo?.affVipTime ?? 0,
            affMoney: userInfo?.affMoney ?? 0,
            affMoneyCondition: userInfo?.affMoneyCondition ?? 0,
            affPercentLv2: userInfo?.affPercentLv2 ?? 0,
          }}
          onSubmit={handleSubmit}
          validateOnBlur
          validateOnChange
          enableReinitialize
        >
          {({ resetForm }) => (
            <Form>
              <div className="flex gap-16">
                <div>
                  <div className="text-sm">Họ và tên</div>
                  <FormControl name="fullName">
                    <Input
                      className="mt-1 !w-[375px]"
                      inputClassName="!bg-[rgba(239,241,249,0.60)] !rounded-xl font-medium !h-[52px] !leading-[52px]"
                    />
                  </FormControl>
                  <div className="text-sm mt-4">Tên đăng nhập</div>
                  <FormControl name="username">
                    <Input
                      className="mt-1 !w-[375px]"
                      inputClassName="!bg-[rgba(239,241,249,0.60)] cursor-not-allowed !text-gray-500 !rounded-xl font-medium !h-[52px] !leading-[52px]"
                      disabled
                    />
                  </FormControl>
                  <div className="text-sm mt-4">Email</div>
                  <FormControl name="email">
                    <Input
                      className="mt-1 !w-[375px]"
                      inputClassName="!bg-[rgba(239,241,249,0.60)] !rounded-xl font-medium !h-[52px] !leading-[52px]"
                    />
                  </FormControl>
                  <div className="text-sm mt-4">Provider</div>
                  <FormControl name="provider">
                    <Input
                      className="mt-1 !w-[375px]"
                      inputClassName="!bg-[rgba(239,241,249,0.60)] cursor-not-allowed !text-gray-500 !rounded-xl font-medium !h-[52px] !leading-[52px]"
                      disabled
                    />
                  </FormControl>
                  <div className="text-sm mt-4">Role</div>
                  <FormControl name="role">
                    <CustomSelect
                      options={RoleOptions}
                      className="bg-color w-full !h-[52px] px-1 mt-1 rounded-lg pr-4"
                      optionContainerClassName="mt-4 rounded-b-lg w-full overflow-y-auto w-36"
                      optionClassName="whitespace-nowrap px-3 py-3 border-b border-solid w-auto"
                      labelSelectedClassName="black-50 !mx-3"
                      placeholder="Role"
                      arrow={<ChevronDownArrow />}
                      hasBgColorHover
                      onChange={handleRoleOptionChange}
                    />
                  </FormControl>
                  <div className="text-sm mt-4">Aff Alias</div>
                  <FormControl name="affAlias">
                    <Input
                      className="mt-1 !w-[375px]"
                      inputClassName="!bg-[rgba(239,241,249,0.60)] !rounded-xl font-medium !h-[52px] !leading-[52px]"
                    />
                  </FormControl>
                  <div className="text-sm mt-4">Aff Vip Time</div>
                  <FormControl name="affVipTime">
                    <Input
                      className="mt-1 !w-[375px]"
                      inputClassName="!bg-[rgba(239,241,249,0.60)] !rounded-xl font-medium !h-[52px] !leading-[52px]"
                    />
                  </FormControl>
                  <div className="text-sm mt-4">Aff Money</div>
                  <FormControl name="affMoney">
                    <Input
                      className="mt-1 !w-[375px]"
                      inputClassName="!bg-[rgba(239,241,249,0.60)] !rounded-xl font-medium !h-[52px] !leading-[52px]"
                    />
                  </FormControl>
                  <div className="text-sm mt-4">Aff Money Condition</div>
                  <FormControl name="affMoneyCondition">
                    <Input
                      className="mt-1 !w-[375px]"
                      inputClassName="!bg-[rgba(239,241,249,0.60)] !rounded-xl font-medium !h-[52px] !leading-[52px]"
                    />
                  </FormControl>
                  <div className="text-sm mt-4">Aff Percent Lv2</div>
                  <FormControl name="affPercentLv2">
                    <Input
                      className="mt-1 !w-[375px]"
                      inputClassName="!bg-[rgba(239,241,249,0.60)] !rounded-xl font-medium !h-[52px] !leading-[52px]"
                    />
                  </FormControl>
                </div>
                <div>
                  {!isSelectedRoleAuthor && (
                    <div className="mb-4">
                      <div className="text-sm">Số điện thoại</div>
                      <FormControl name="phone">
                        <Input
                          className="mt-1 !w-[375px]"
                          inputClassName="!bg-[rgba(239,241,249,0.60)] !rounded-xl font-medium !h-[52px] !leading-[52px]"
                        />
                      </FormControl>
                    </div>
                  )}
                  <div className="text-sm">Loại</div>
                  <FormControl name="isVip">
                    <CustomSelect
                      options={UserOptions}
                      className="bg-color w-full !h-[52px] px-1 mt-1 rounded-lg pr-4 !cursor-not-allowed"
                      optionContainerClassName="mt-4 rounded-b-lg w-full overflow-y-auto w-36"
                      optionClassName="whitespace-nowrap px-3 border-b border-solid w-auto"
                      labelSelectedClassName="black-50 !mx-3"
                      placeholder="Loại"
                      arrow={<ChevronDownArrow />}
                      hasBgColorHover
                      disabled
                    />
                  </FormControl>
                  <div className="text-sm mt-4">Vas</div>
                  <FormControl name="point">
                    <Input
                      className="mt-1 !w-[375px]"
                      inputClassName="!bg-[rgba(239,241,249,0.60)] cursor-not-allowed !text-gray-500 !rounded-xl font-medium !h-[52px] !leading-[52px]"
                      disabled
                    />
                  </FormControl>
                  <div className="text-sm mt-4">Status</div>
                  <FormControl name="status">
                    <CustomSelect
                      options={StatusOptions}
                      className="bg-color w-full !h-[52px] px-1 mt-1 rounded-lg pr-4"
                      optionContainerClassName="mt-4 rounded-b-lg w-full overflow-y-auto w-36"
                      optionClassName="whitespace-nowrap px-3 py-3 border-b border-solid w-auto"
                      labelSelectedClassName="black-50 !mx-3"
                      placeholder="Trạng thái"
                      arrow={<ChevronDownArrow />}
                      hasBgColorHover
                    />
                  </FormControl>
                  {showMaxVoiceTrain && (
                    <>
                      <div className="text-sm mt-4">Max Voice Train</div>
                      <FormControl name="maxVoiceTrain">
                        <Input
                          className="mt-1 !w-[375px]"
                          placeholder="Max Voice Train"
                          inputClassName="!bg-[rgba(239,241,249,0.60)] !rounded-xl font-medium !h-[52px] !leading-[52px]"
                          type="number"
                        />
                      </FormControl>
                      <div className="mt-4">
                        <FormControl name="canCreateAudio">
                          <Checkbox
                            label="Cho phép author tạo audio AI từ văn bản"
                            labelClassName="text-sm text-[#272C36]"
                          />
                        </FormControl>
                      </div>
                      <div className="mt-4">
                        <FormControl name="canUploadAudio">
                          <Checkbox
                            label="Cho phép author upload audio có sẵn"
                            labelClassName="text-sm text-[#272C36]"
                          />
                        </FormControl>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="flex justify-between">
                <div className="flex">
                  <Button
                    containerClassName="mt-[60px]"
                    width="auto"
                    type="submit"
                    className="!w-[118px] !h-[52px] font-medium !rounded-xl"
                    label="Cập nhật"
                  />
                  <Button
                    containerClassName="mt-[60px] ml-6"
                    width="auto"
                    className="!w-[118px] !h-[52px] font-medium !rounded-xl"
                    label="Hủy"
                    theme="secondary"
                    onClick={() => resetForm()}
                  />
                </div>
                <div className="flex">
                  <Button
                    containerClassName="mt-[60px] mr-6"
                    width="auto"
                    className="!w-[158px] !h-[52px] font-medium !rounded-xl"
                    label="Reset Password"
                    onClick={handleReset}
                  />
                  {userInfo?.isVip && (
                    <Button
                      containerClassName="mt-[60px] "
                      width="auto"
                      className="!w-[158px] !h-[52px] font-medium !rounded-xl"
                      label="Thu hồi VIP"
                      theme="secondary"
                      onClick={handleRevoke}
                    />
                  )}
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default InfoUserTab;
